import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LockKey, SignOut, DownloadSimple, ArrowClockwise, CheckCircle, Circle } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "sa_admin_token";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "open", label: "Unactioned" },
  { id: "done", label: "Actioned" },
];

export default function AdminPage() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("open");
  const [savingId, setSavingId] = useState(null);

  const fetchEnquiries = async (tk) => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/admin/enquiries`, {
        headers: { "X-Admin-Token": tk },
      });
      setEnquiries(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        setError("Wrong password");
        sessionStorage.removeItem(TOKEN_KEY);
        setToken("");
      } else {
        setError(err?.response?.data?.detail || "Couldn't load enquiries");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const { data } = await axios.get(`${API}/admin/enquiries`, {
          headers: { "X-Admin-Token": token },
        });
        if (!cancelled) setEnquiries(data);
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 401) {
          sessionStorage.removeItem(TOKEN_KEY);
          setToken("");
          setError("Session expired — sign in again.");
        } else {
          setError(err?.response?.data?.detail || "Couldn't load enquiries");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const onLogin = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setPassword("");
    } catch (err) {
      setError(err?.response?.data?.detail || "Login failed");
    }
  };

  const onLogout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setEnquiries([]);
  };

  const exportCsv = () => {
    if (!enquiries.length) return toast.error("Nothing to export.");
    const cols = ["created_at", "name", "email", "phone", "accommodation_type", "check_in", "check_out", "adults", "children", "guests", "has_pet", "message", "actioned"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""').replace(/\n/g, " ")}"`;
    const csv = [cols.join(","), ...enquiries.map((e) => cols.map((c) => esc(e[c])).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `shady-acres-enquiries-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  const toggleActioned = async (id, next) => {
    setSavingId(id);
    try {
      const { data } = await axios.patch(
        `${API}/admin/enquiries/${id}`,
        { actioned: next },
        { headers: { "X-Admin-Token": token } },
      );
      setEnquiries((list) => list.map((e) => (e.id === id ? { ...e, actioned: data.actioned } : e)));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update enquiry");
    } finally {
      setSavingId(null);
    }
  };

  const unactionedCount = useMemo(() => enquiries.filter((e) => !e.actioned).length, [enquiries]);
  const visible = useMemo(() => {
    if (filter === "open") return enquiries.filter((e) => !e.actioned);
    if (filter === "done") return enquiries.filter((e) => e.actioned);
    return enquiries;
  }, [enquiries, filter]);

  // ---------- Login screen ----------
  if (!token) {
    return (
      <section className="max-w-md mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-32" data-testid="admin-login">
        <div className="overline">Admin</div>
        <h1 className="font-serif text-4xl md:text-5xl tracking-tight mt-4">Shady Acres dashboard</h1>
        <p className="mt-3 text-sa-ink2">Enter the admin password to view all guest enquiries.</p>

        <form onSubmit={onLogin} className="mt-10 bg-white border border-sa-ink/10 rounded-2xl p-8">
          <label className="overline block mb-2">Password</label>
          <div className="relative">
            <LockKey size={18} weight="duotone" className="absolute left-3 top-1/2 -translate-y-1/2 text-sa-ink2" />
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-12 w-full bg-white border border-sa-ink/15 rounded-md pl-10 pr-4 text-sa-ink focus:border-sa-ink focus:outline-none"
              placeholder="••••••••"
              data-testid="admin-password"
              autoFocus
            />
          </div>
          {error && <p className="mt-3 text-sm text-sa-accent" data-testid="admin-error">{error}</p>}
          <button type="submit" className="btn-primary w-full mt-6" data-testid="admin-login-btn">
            Sign in
          </button>
        </form>
      </section>
    );
  }

  // ---------- Dashboard ----------
  return (
    <section className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-32" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="overline">Admin</div>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight mt-2">Enquiries</h1>
          <p className="mt-2 text-sa-ink2">
            {loading ? "Loading…" : `${enquiries.length} total · ${unactionedCount} unactioned`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => fetchEnquiries(token)}
            className="h-11 px-4 rounded-md border border-sa-ink/15 bg-white hover:border-sa-ink/40 flex items-center gap-2 text-sm"
            data-testid="admin-refresh"
          >
            <ArrowClockwise size={16} weight="bold" /> Refresh
          </button>
          <button
            onClick={exportCsv}
            className="h-11 px-4 rounded-md border border-sa-ink/15 bg-white hover:border-sa-ink/40 flex items-center gap-2 text-sm"
            data-testid="admin-export"
          >
            <DownloadSimple size={16} weight="bold" /> Export CSV
          </button>
          <button
            onClick={onLogout}
            className="h-11 px-4 rounded-md bg-sa-ink text-sa-bg hover:bg-sa-ink/90 flex items-center gap-2 text-sm"
            data-testid="admin-logout"
          >
            <SignOut size={16} weight="bold" /> Sign out
          </button>
        </div>
      </div>

      {error && <p className="mt-6 text-sm text-sa-accent">{error}</p>}

      <div className="mt-8 flex items-center gap-2 flex-wrap" data-testid="admin-filters">
        {FILTERS.map((f) => {
          const active = filter === f.id;
          const count = f.id === "open" ? unactionedCount : f.id === "done" ? enquiries.length - unactionedCount : enquiries.length;
          return (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`h-9 px-4 rounded-full text-sm border transition-colors ${
                active
                  ? "bg-sa-ink text-sa-bg border-sa-ink"
                  : "bg-white text-sa-ink2 border-sa-ink/15 hover:border-sa-ink/40"
              }`}
              data-testid={`admin-filter-${f.id}`}
            >
              {f.label} <span className={active ? "text-sa-bg/70" : "text-sa-ink2/60"}>· {count}</span>
            </button>
          );
        })}
      </div>

      <div className="mt-6 bg-white border border-sa-ink/10 rounded-2xl overflow-hidden" data-testid="admin-table-wrap">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-sa-bg2 text-sa-ink2">
              <tr>
                <th className="text-left font-medium px-5 py-4 w-12">Done</th>
                <th className="text-left font-medium px-5 py-4">Received</th>
                <th className="text-left font-medium px-5 py-4">Guest</th>
                <th className="text-left font-medium px-5 py-4">Contact</th>
                <th className="text-left font-medium px-5 py-4">Stay</th>
                <th className="text-left font-medium px-5 py-4">Dates</th>
                <th className="text-left font-medium px-5 py-4">Guests</th>
                <th className="text-left font-medium px-5 py-4">Pet</th>
                <th className="text-left font-medium px-5 py-4">Message</th>
              </tr>
            </thead>
            <tbody>
              {!loading && visible.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-5 py-12 text-center text-sa-ink2">
                    {filter === "open" ? "Inbox zero — nice work." : filter === "done" ? "No actioned enquiries yet." : "No enquiries yet."}
                  </td>
                </tr>
              )}
              {visible.map((e) => (
                <tr
                  key={e.id}
                  className={`border-t border-sa-ink/8 align-top transition-colors ${e.actioned ? "bg-sa-bg2/40 text-sa-ink2" : ""}`}
                  data-testid={`admin-row-${e.id}`}
                >
                  <td className="px-5 py-4">
                    <button
                      onClick={() => toggleActioned(e.id, !e.actioned)}
                      disabled={savingId === e.id}
                      className="text-sa-accent hover:text-sa-accentHover disabled:opacity-50 transition-colors"
                      aria-label={e.actioned ? "Mark as unactioned" : "Mark as actioned"}
                      data-testid={`admin-toggle-${e.id}`}
                      title={e.actioned ? "Mark as unactioned" : "Mark as actioned"}
                    >
                      {e.actioned
                        ? <CheckCircle size={24} weight="fill" />
                        : <Circle size={24} weight="regular" />}
                    </button>
                  </td>
                  <td className="px-5 py-4 whitespace-nowrap text-sa-ink2">
                    {new Date(e.created_at).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })}
                  </td>
                  <td className={`px-5 py-4 ${e.actioned ? "" : "font-medium"}`}>{e.name}</td>
                  <td className="px-5 py-4">
                    <div><a href={`mailto:${e.email}`} className="text-sa-accent hover:underline">{e.email}</a></div>
                    {e.phone && <div className="text-sa-ink2 text-xs mt-1">{e.phone}</div>}
                  </td>
                  <td className="px-5 py-4 text-sa-ink2">{e.accommodation_type || "—"}</td>
                  <td className="px-5 py-4 text-sa-ink2 whitespace-nowrap">
                    {e.check_in || "—"}<br />→ {e.check_out || "—"}
                  </td>
                  <td className="px-5 py-4 text-sa-ink2 whitespace-nowrap">
                    {(e.adults !== undefined && e.adults !== null)
                      ? <>{e.adults}<span className="text-xs"> adult{e.adults === 1 ? "" : "s"}</span>{e.children > 0 && <>{" · "}{e.children}<span className="text-xs"> child{e.children === 1 ? "" : "ren"}</span></>}</>
                      : (e.guests ?? "—")}
                  </td>
                  <td className="px-5 py-4 text-sa-ink2 whitespace-nowrap">
                    {e.has_pet ? <span className="inline-block px-2 py-0.5 text-xs rounded-full bg-sa-accent/15 text-sa-accent">Yes</span> : <span className="text-xs">—</span>}
                  </td>
                  <td className="px-5 py-4 text-sa-ink2 max-w-md">
                    <div className="line-clamp-3 whitespace-pre-wrap">{e.message}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
